import cloneDeep from 'lodash.clonedeep';
import { getActivitiesWithStudentInfo } from '../../../../services/API';
import { getScreeningWindowsForSchoolYear } from '../../../../services/screeningWindowService';

import { generateBenchmarkReport } from './generateBenchmarkReport';
import { getPR } from '../../functions/getPR';
import { states } from '../../values/enums';

export async function generateClassProgressReport(params) {
  const {
    students,
    limit,
    type,
    equating,
    selectedLocale,
    activities,
    setClassProgressReduxState,
  } = params;

  const windows = getScreeningWindowsForSchoolYear();

  const studentActivitiesPromises = [];

  // iterate through each window of the school year
  // and generate benchmark/latest scores for each student
  for (const window of windows) {
    const {startDate, endDate} = window;
    const statuses = ["not_started","scored"]
    const displayStatuses = ["completed","rescored","prereader"]
    const studentActivities = getActivitiesWithStudentInfo(students, limit, type, statuses, equating, selectedLocale, startDate, endDate, displayStatuses);
    // CACHE student activities for each window
    studentActivitiesPromises.push(studentActivities)
  }

  const studentActivitiesForYear = await Promise.all(studentActivitiesPromises);
  const updatedActivities = cloneDeep(activities);
  // add activities to the respective locale
  updatedActivities[selectedLocale] = studentActivitiesForYear;
  setClassProgressReduxState({activities: updatedActivities});

}

export async function generateClassProgressReportMetrics(params) {
  const {
    students,
    metricId,
    scaleId,
    selectedBenchmark,
    customBenchmarks,
    selectedLocale,
    isBenchmarkOnly,
    activities,
  } = params;
  const classProgressReport = {};
  const windows = getScreeningWindowsForSchoolYear();
  for (const [index, window] of windows.entries()) {
    const studentActivities = cloneDeep(activities[selectedLocale][index]);
    const classProgressParams = {
      studentActivities,
      metricId,
      scaleId,
      selectedBenchmark,
      customBenchmarks,
      selectedLocale,
      students,
      isBenchmarkOnly,
      window
    };
    const classProgressReportWindow = await generateClassProgressReportWindow(classProgressParams);
    const {benchmark, latest} = classProgressReportWindow;

    const {displayCutoffs, scores: benchmarkScores} = benchmark;
    for (const benchmarkScore of benchmarkScores) {
      if (benchmarkScore.status === states.ERROR || benchmarkScore.status === "UNDER_REVIEW" || benchmarkScore.displayScore === null) continue;
      const {ui, displayScore: score} = benchmarkScore;
      const {label, value} = ui;
      let cutOff = "AVERAGE";
      if (score >= displayCutoffs["GOOD"]) cutOff = "GOOD";
      if (score < displayCutoffs["POOR"]) cutOff = "POOR";
      if (!(value in classProgressReport)) classProgressReport[value] = {};
      const formattedScore = score ? Math.round(score * 100)/100 : score;
      const PR = getPR({
        score,
        scale: scaleId,
        grade: selectedBenchmark.grade,
        language: selectedLocale,
        window: window.id,
      });
      classProgressReport[value][window.id] = { name: label, score: formattedScore, cutOff, PR, scaleId };
    }

    // compute latest scores if latest assessment is checked
    if (!isBenchmarkOnly) {
      const {scores: latestScores} = latest;
      for (const latestScore of latestScores) {
        if (latestScore.status === states.ERROR || latestScore.status === "UNDER_REVIEW" || latestScore.displayScore === null) continue;
        const {ui, displayScore: score} = latestScore;
        const {value} = ui;

        if (!(value in classProgressReport)) {
            classProgressReport[value] = {};
            classProgressReport[value][window.id] = {name: ui.label};
        }

        const formattedLatestScore = score ? Math.round(score * 100)/100 : score;
        if (!classProgressReport[value][window.id]) classProgressReport[value][window.id] = {};
        classProgressReport[value][window.id].latestScore = formattedLatestScore;
      }
    }
  }
  const classProgressReportMetrics = Object.entries(classProgressReport).map(([studentId, scores]) => ({studentId, scores, selectedBenchmark, selectedLocale}));
  // get list of studentIds with activities from report
  const studentIds = classProgressReportMetrics.map(({studentId}) => studentId);

  // add students with no activities into report
  for (const student of students) {
    if (!studentIds.includes(student.id)) {
      classProgressReportMetrics.push({
        studentId: student.id,
        scores: {
          BOY: {
            name: student.ui.label
          }
        },
        selectedBenchmark,
        selectedLocale,
      });
    }
  }

  return classProgressReportMetrics;
}

async function generateClassProgressReportWindow(params) {
  const {
    studentActivities: activities,
    metricId,
    scaleId,
    selectedBenchmark: benchmarkId,
    customBenchmarks,
    selectedLocale,
    students,
    isBenchmarkOnly,
    window
  } = params;

  const classProgressReportWindow = {};
  // generate the benchmark report for the class (BENCHMARK activites only)
  classProgressReportWindow.benchmark = await generateBenchmarkReport(activities, metricId, scaleId, benchmarkId, customBenchmarks, selectedLocale, students, true, window);

  // generate report with latest scores if option is selected from teacher selections sidebar
  if (!isBenchmarkOnly) {
    classProgressReportWindow.latest = await generateBenchmarkReport(activities, metricId, scaleId, benchmarkId, customBenchmarks, selectedLocale, students, false, window);
  }

  return classProgressReportWindow;
}
